import React from 'react';
import { Row, Col, Container, Spinner, Alert } from 'react-bootstrap';
import PropertyCard, { Property } from './PropertyCard';

interface PropertyGridProps {
  properties: Property[];
  loading?: boolean;
  error?: string | null;
  onViewDetails?: (property: Property) => void;
  onFavorite?: (propertyId: string) => void;
  favoriteProperties?: string[];
}

const PropertyGrid: React.FC<PropertyGridProps> = ({ 
  properties, 
  loading = false,
  error = null,
  onViewDetails,
  onFavorite,
  favoriteProperties = []
}) => {
  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading properties...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Properties</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (properties.length === 0) {
    return (
      <Container className="py-5">
        <Alert variant="info">
          <Alert.Heading>No Properties Found</Alert.Heading>
          <p>Try adjusting your search filters to find more properties.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="property-grid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          Found {properties.length} {properties.length === 1 ? 'property' : 'properties'}
        </h5>
        <div className="d-flex gap-2">
          <select className="form-select form-select-sm" style={{ width: 'auto' }}>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="newest">Newest First</option>
            <option value="beds">Most Bedrooms</option>
            <option value="sqft">Largest First</option>
          </select>
        </div>
      </div>

      <Row className="g-4">
        {properties.map(property => (
          <Col key={property.id} xs={12} sm={6} lg={4} xl={3}>
            <PropertyCard
              property={property}
              onViewDetails={onViewDetails}
              onFavorite={onFavorite}
              isFavorite={favoriteProperties.includes(property.id)}
            />
          </Col>
        ))}
      </Row>

      {properties.length > 12 && (
        <div className="text-center mt-5">
          <p className="text-muted">
            Showing {Math.min(12, properties.length)} of {properties.length} properties
          </p>
        </div>
      )}
    </Container>
  );
};

export default PropertyGrid;